import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Alert,
    IconButton,
    CircularProgress,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RequestService from '../../services/RequestService';

const initialState = {
    title: '',
    message: '',
    target: 'SYSTEM',
    eventId: '',
};

export default function EditNotificationCampaignDialog({
    open,
    onClose,
    onSuccess,
    campaign,
}) {
    const [formData, setFormData] = useState(initialState);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [events, setEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(false);

    const isAlreadySent = !!campaign?.sentAt;
    const disabledAll = submitting || isAlreadySent;

    useEffect(() => {
        if (!open) return;
        if (formData.target === 'EVENT') loadEvents();
        // eslint-disable-next-line
    }, [open, formData.target]);

    useEffect(() => {
        if (!open) return;
        if (!campaign) return;

        const hasEvent = !!campaign.eventId;

        setFormData({
            title: campaign.title || '',
            message: campaign.message || '',
            target: hasEvent ? 'EVENT' : 'SYSTEM',
            eventId: hasEvent ? String(campaign.eventId) : '',
        });

        setError(null);
        setSubmitting(false);

        if (hasEvent) loadEvents();
        // eslint-disable-next-line
    }, [open, campaign?.id]);

    const loadEvents = async () => {
        try {
            setLoadingEvents(true);

            const data = await RequestService.getEvents({ page: 0, size: 100 });

            setEvents(data.content || data.events || data || []);
        } catch (err) {
            console.error('Erro ao carregar eventos:', err);
            setEvents([]);
        } finally {
            setLoadingEvents(false);
        }
    };

    const resetForm = () => {
        setFormData(initialState);
        setError(null);
        setSubmitting(false);
        setEvents([]);
        setLoadingEvents(false);
    };

    const handleClose = (evt, reason) => {
        if (reason === 'backdropClick') return;
        resetForm();
        onClose?.(evt, reason);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleTargetChange = (e, newTarget) => {
        if (!newTarget) return;
        setFormData((prev) => ({
            ...prev,
            target: newTarget,
            eventId: newTarget === 'EVENT' ? prev.eventId : '',
        }));
    };

    const validate = () => {
        if (!formData.title.trim()) return 'Informe um título para a mensagem.';
        if (formData.title.length > 150)
            return 'O título deve ter no máximo 150 caracteres.';

        if (!formData.message.trim()) return 'Informe o conteúdo da mensagem.';
        if (formData.message.length > 255)
            return 'A mensagem deve ter no máximo 255 caracteres.';

        if (formData.target === 'EVENT' && !formData.eventId)
            return 'Selecione um evento.';

        return null;
    };

    const updatePayload = useMemo(() => {
        const hasEvent = formData.target === 'EVENT' && !!formData.eventId;
        return {
            title: formData.title.trim(),
            message: formData.message.trim(),
            eventId: hasEvent ? parseInt(formData.eventId, 10) : null,
        };
    }, [formData.title, formData.message, formData.target, formData.eventId]);

    const handleSubmit = async () => {
        setError(null);

        if (!campaign?.id) {
            setError('Campanha inválida.');
            return;
        }

        if (isAlreadySent) {
            setError('Essa campanha já foi enviada e não pode ser editada.');
            return;
        }

        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        try {
            setSubmitting(true);

            await RequestService.updateNotificationCampaign(
                campaign.id,
                updatePayload
            );

            onSuccess?.('Campanha atualizada com sucesso!');
            resetForm();
            onClose?.();
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data ||
                'Erro ao atualizar a campanha.';
            setError(
                typeof message === 'string' ? message : 'Erro ao atualizar.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const isEventFlow = formData.target === 'EVENT';

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle>
                Editar Campanha
                <IconButton
                    aria-label='close'
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        mt: 1,
                    }}
                >
                    {isAlreadySent && (
                        <Alert severity='info'>
                            Esta campanha já foi enviada e não pode ser editada.
                        </Alert>
                    )}

                    <TextField
                        label='Título'
                        name='title'
                        value={formData.title}
                        onChange={handleChange}
                        fullWidth
                        disabled={disabledAll}
                        inputProps={{ maxLength: 150 }}
                        helperText={`${formData.title.length}/150 caracteres`}
                    />

                    <TextField
                        label='Mensagem'
                        name='message'
                        value={formData.message}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={4}
                        disabled={disabledAll}
                        inputProps={{ maxLength: 255 }}
                        helperText={`${formData.message.length}/255 caracteres`}
                    />

                    <Box>
                        <InputLabel sx={{ mb: 1 }}>Tipo da mensagem</InputLabel>

                        <ToggleButtonGroup
                            value={formData.target}
                            exclusive
                            onChange={handleTargetChange}
                            disabled={disabledAll}
                            fullWidth
                            sx={{ display: 'flex' }}
                        >
                            <ToggleButton value='SYSTEM' sx={{ flex: 1 }}>
                                Geral
                            </ToggleButton>
                            <ToggleButton value='EVENT' sx={{ flex: 1 }}>
                                Evento
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {isEventFlow && (
                        <FormControl
                            fullWidth
                            disabled={disabledAll || loadingEvents}
                        >
                            <InputLabel>Evento</InputLabel>
                            <Select
                                name='eventId'
                                value={formData.eventId}
                                onChange={handleChange}
                                label='Evento'
                            >
                                <MenuItem value=''>
                                    <em>Selecione</em>
                                </MenuItem>

                                {events.map((ev) => (
                                    <MenuItem key={ev.id} value={String(ev.id)}>
                                        {ev.title}
                                    </MenuItem>
                                ))}
                            </Select>

                            <FormHelperText>
                                {loadingEvents
                                    ? 'Carregando eventos...'
                                    : 'Selecione qual evento será aberto ao clicar na notificação'}
                            </FormHelperText>
                        </FormControl>
                    )}

                    {error && <Alert severity='error'>{error}</Alert>}
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleClose}
                    variant='outlined'
                    disabled={submitting}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleSubmit}
                    color='primary'
                    variant='contained'
                    disabled={disabledAll}
                    endIcon={submitting ? <CircularProgress size={18} /> : null}
                >
                    {submitting ? 'Salvando...' : 'Salvar alterações'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
